/**
 * Composable for tree diffing functionality
 * @returns {Object} Object containing the diffTree and diffSettings functions
 */
export const useTreeDiff = () => {
  /**
   * Flattens a tree structure into a Map for easier comparison
   * @param {Object} node - The tree node to flatten
   * @param {Map} map - The map to store flattened nodes
   * @param {string} parentId - The ID of the parent node
   * @returns {Map} The map containing flattened nodes
   */
  const flatMap = (node, map = new Map(), parentId = null) => {
    if (!node) return map;

    const { children, ...rest } = node;
    if (node.code && !node.id) {
      map.set(node.code, { ...rest, parentId });
    }

    if (Array.isArray(node.children)) {
      node.children.forEach((child) => flatMap(child, map, node.type === 'group' ? node.uuid : null));
    }
    return map;
  };

  /**
   * Compares two tree structures and returns the differences
   * @param {Object} oldNode - The previous tree state
   * @param {Object} newNode - The new tree state
   * @returns {Object} Object containing added, removed, and modified nodes
   */
  const diffTree = (oldNode, newNode) => {
    const added = [];
    const removed = [];
    const modified = [];

    const oldMap = flatMap(oldNode);
    const newMap = flatMap(newNode);

    // Detect added and modified nodes
    for (const [key, newItem] of newMap.entries()) {
      const oldItem = oldMap.get(key);
      if (!oldItem) {
        added.push(newItem);
      } else if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
        modified.push({
          old: oldItem,
          new: newItem,
          changed: Object.keys(newItem).reduce((acc, key) => {
            if (JSON.stringify(oldItem[key]) !== JSON.stringify(newItem[key])) {
              acc[key] = { old: oldItem[key], new: newItem[key] };
            }
            return acc;
          }, {}),
        });
      }
    }

    // Detect removed nodes
    for (const key of oldMap.keys()) {
      if (!newMap.has(key)) {
        removed.push(oldMap.get(key));
      }
    }

    return { added, removed, modified };
  };

  /**
   * Compares two simple key-value objects and returns the differences
   * @param {Object} oldSettings - The previous settings state
   * @param {Object} newSettings - The new settings state
   * @returns {Object} Object containing added, removed, and modified settings
   */
  const diffSettings = (oldSettings, newSettings) => {
    const modified = [];

    // Compare all keys in both objects
    const allKeys = new Set([...Object.keys(oldSettings || {}), ...Object.keys(newSettings || {})]);

    for (const key of allKeys) {
      const oldValue = oldSettings?.[key];
      const newValue = newSettings?.[key];

      if (oldValue === undefined) {
        modified.push({ key, value: newValue });
      } else if (newValue === undefined) {
        modified.push({ key, value: oldValue });
      } else if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        modified.push({ key, value: newValue });
      }
    }

    return { modified };
  };

  /**
   * Compares two arrays of comments and returns the differences
   * @param {Array} oldComments - The previous comments array
   * @param {Array} newComments - The new comments array
   * @returns {Object} Object containing added, removed, and modified comments
   */
  const diffComments = (oldComments, newComments) => {
    const added = [];
    const modified = [];
    const removed = [];

    // Convert arrays to maps for easier comparison
    const oldMap = new Map((oldComments || []).map((comment) => [comment.data.code, comment.data]));
    const newMap = new Map((newComments || []).map((comment) => [comment.data.code, comment.data]));

    // Detect added and modified comments
    for (const [id, newData] of newMap.entries()) {
      const oldData = oldMap.get(id);
      if (!oldData) {
        added.push({ key: id, value: newData });
      } else if (JSON.stringify(oldData) !== JSON.stringify(newData)) {
        modified.push({ key: id, value: newData });
      }
    }

    // Detect removed comments
    for (const [id, oldData] of oldMap.entries()) {
      if (!newMap.has(id)) {
        removed.push({ key: id, value: oldData });
      }
    }

    return { added, removed, modified };
  };

  const diffArray = (oldArray, newArray) => {
    const added = [];
    const removed = [];
    const modified = [];

    // Convert arrays to maps for easier comparison using 'code' as the key
    const oldMap = new Map((oldArray || []).map((item) => [item.code, item]));
    const newMap = new Map((newArray || []).map((item) => [item.code, item]));

    // Detect added and modified items
    for (const [code, newItem] of newMap.entries()) {
      const oldItem = oldMap.get(code);
      if (!oldItem) {
        added.push(newItem);
      } else if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
        modified.push({
          old: oldItem,
          new: newItem,
        });
      }
    }

    // Detect removed items
    for (const [code, oldItem] of oldMap.entries()) {
      if (!newMap.has(code)) {
        removed.push(oldItem);
      }
    }

    return { added, removed, modified };
  };

  return { diffTree, diffSettings, diffComments, diffArray };
};
